import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getMyDashboardAccessRequests, submitDashboardAccessRequest } from "../../../api/dashboardAccessRequestsApi";
import { MyDashboardAccessRequest } from "../../../pages/Settings/components/myDashboardAccessRequestsMockData";

export const MY_DASHBOARD_ACCESS_REQUESTS_QUERY_KEY = ["dashboard-access-requests", "me"];

// Short staleTime (unlike the dashboard registry's Infinity) - this should reflect a just-submitted
// request without requiring a full page reload.
export const useMyDashboardAccessRequests = () => {
  const { data, isLoading } = useQuery<MyDashboardAccessRequest[]>(MY_DASHBOARD_ACCESS_REQUESTS_QUERY_KEY, getMyDashboardAccessRequests, {
    staleTime: 0,
  });
  return { myRequests: data ?? [], isLoading };
};

// POST /user_service/dashboard-access-requests is live - submits directly (dashboardId is the
// dashboard's real numeric id, §1). The legacy GET /user_service/request-dashboard-access every
// call site used to go through instead has been deleted along with its frontend wrapper
// (src/api/user.ts's requestDashboardAccess) - see useRequestAccessByPath.ts for call sites that
// only know a dashboard by path. Normalizes the richer POST response down to
// MyDashboardAccessRequest and appends it to the cached "me" list so the pending state shows up
// immediately, without a refetch.
export const useSubmitDashboardAccessRequest = () => {
  const queryClient = useQueryClient();
  return useMutation((dashboardId: number) => submitDashboardAccessRequest(dashboardId), {
    onSuccess: (response) => {
      const newRequest: MyDashboardAccessRequest = {
        id: response.id,
        dashboard_id: response.dashboard_id,
        status: response.status,
        requested_at: response.requested_at,
        decided_at: null,
      };
      queryClient.setQueryData<MyDashboardAccessRequest[]>(MY_DASHBOARD_ACCESS_REQUESTS_QUERY_KEY, (current) => [
        ...(current ?? []),
        newRequest,
      ]);
    },
  });
};
